import { SchoolLocation } from 'context/SchoolContext';

export interface LocalBusinessData {
  name: string;
  description?: string;
  url: string;
  telephone?: string;
  email?: string;
  logo?: string;
  image?: string;
  address?: {
    streetAddress: string;
    addressLocality: string;
    addressRegion: string;
    postalCode: string;
    addressCountry?: string;
  };
  geo?: {
    latitude: number;
    longitude: number;
  };
  openingHours?: string[];
  priceRange?: string;
  areaServed?: string[];
}

export interface ServiceData {
  name: string;
  description: string;
  provider: string;
  areaServed?: string[];
  offers?: {
    price?: string;
    priceCurrency?: string;
    availability?: string;
    url?: string;
  };
}

export interface FAQData {
  question: string;
  answer: string;
}

export const createLocalBusinessSchema = (data: LocalBusinessData) => {
  const schema: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'DrivingSchool',
    name: data.name,
    url: data.url,
    ...(data.description && { description: data.description }),
    ...(data.telephone && { telephone: data.telephone }),
    ...(data.email && { email: data.email }),
    ...(data.logo && { logo: data.logo }),
    ...(data.image && { image: data.image }),
    ...(data.priceRange && { priceRange: data.priceRange }),
  };

  if (data.address) {
    schema.address = {
      '@type': 'PostalAddress',
      streetAddress: data.address.streetAddress,
      addressLocality: data.address.addressLocality,
      addressRegion: data.address.addressRegion,
      postalCode: data.address.postalCode,
      addressCountry: data.address.addressCountry || 'US',
    };
  }

  if (data.geo) {
    schema.geo = {
      '@type': 'GeoCoordinates',
      latitude: data.geo.latitude,
      longitude: data.geo.longitude,
    };
  }

  if (data.openingHours) {
    schema.openingHours = data.openingHours;
  }

  if (data.areaServed) {
    schema.areaServed = data.areaServed.map(area => ({
      '@type': 'City',
      name: area,
    }));
  }

  return schema;
};

export const createServiceSchema = (data: ServiceData) => {
  const schema: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: data.name,
    description: data.description,
    provider: {
      '@type': 'Organization',
      name: data.provider,
    },
    serviceType: 'Driver Education',
    category: 'Education',
  };

  if (data.areaServed) {
    schema.areaServed = data.areaServed.map(area => ({
      '@type': 'City',
      name: area,
    }));
  }

  if (data.offers) {
    schema.offers = {
      '@type': 'Offer',
      ...(data.offers.price && { price: data.offers.price }),
      ...(data.offers.priceCurrency && { priceCurrency: data.offers.priceCurrency }),
      ...(data.offers.availability && { availability: `https://schema.org/${data.offers.availability}` }),
      ...(data.offers.url && { url: data.offers.url }),
    };
  }

  return schema;
};

export const createFAQSchema = (faqs: FAQData[]) => {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(faq => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };
};

export const createWebsiteSchema = (name: string, url: string, description?: string) => {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name,
    url,
    ...(description && { description }),
    potentialAction: {
      '@type': 'SearchAction',
      target: `${url}/search?q={search_term_string}`,
      'query-input': 'required name=search_term_string',
    },
  };
};

export const createOrganizationSchema = (
  name: string,
  url: string,
  logo?: string,
  locations?: SchoolLocation[],
  description?: string
) => {
  const schema: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'EducationalOrganization',
    name,
    url,
    ...(description && { description }),
    ...(logo && { logo }),
    educationalCredentialAwarded: 'Driver Education Certificate',
  };

  if (locations && locations.length > 0) {
    schema.location = locations.map(location => ({
      '@type': 'Place',
      name: `${name} - ${location.city}`,
      address: {
        '@type': 'PostalAddress',
        streetAddress: location.streetAddress,
        addressLocality: location.city,
        addressRegion: location.state,
        postalCode: location.zip,
        addressCountry: 'US',
      },
      ...(location.phone && { telephone: location.phone }),
    }));
  }

  return schema;
};